import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';

export type FilterFieldOption = { value: string; label: string };

export type FilterFieldConfig =
  | {
      id: string;
      type: 'select';
      label: string;
      options: FilterFieldOption[];
    }
  | {
      id: string;
      type: 'search';
      label: string;
      placeholder?: string;
    }
  | {
      id: string;
      type: 'custom';
      label?: string;
      render: (ctx: {
        value: string;
        onChange: (value: string) => void;
      }) => ReactNode;
    };

export type FilterValues = Record<string, string>;

export interface FilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título del sheet. */
  title?: string;
  /** Campos editables en el sheet. */
  fields: FilterFieldConfig[];
  /** Valores aplicados (fuente de verdad para queries / chips). */
  values: FilterValues;
  /** Commit al tocar Aplicar. */
  onApply: (next: FilterValues) => void;
  /** Limpia todos los campos a `emptyValues` o strings vacíos. */
  onClearAll?: () => void;
  emptyValues?: FilterValues;
  /** Label del botón trigger (default i18n). */
  triggerLabel?: string;
  /** Si false, no renderiza el botón (el consumer abre con `onOpenChange`). */
  showTrigger?: boolean;
  className?: string;
}

/**
 * Bottom sheet de filtros para mobile.
 *
 * - Edición en draft mientras está abierto; Aplicar committea; Cancelar/Esc/overlay descarta.
 * - En desktop el consumer suele ocultar este bloque y usar DomainScopeBar.
 *
 * Props documentadas en el PR de US filter-sheet (FASE 1 baseline).
 */
export function FilterSheet({
  open,
  onOpenChange,
  title,
  fields,
  values,
  onApply,
  onClearAll,
  emptyValues,
  triggerLabel,
  showTrigger = true,
  className = '',
}: FilterSheetProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<FilterValues>(values);

  useEffect(() => {
    if (open) {
      setDraft(values);
    }
  }, [open, values]);

  const closeWithoutApply = useCallback(() => {
    setDraft(values);
    onOpenChange(false);
  }, [onOpenChange, values]);

  useKeyEsc(closeWithoutApply, open);

  const setField = (id: string, value: string) => {
    setDraft((prev) => ({ ...prev, [id]: value }));
  };

  const activeCount = fields.reduce((count, field) => {
    const v = values[field.id];
    if (v === undefined || v === '') return count;
    const empty = emptyValues?.[field.id];
    if (empty !== undefined && v === empty) return count;
    return count + 1;
  }, 0);

  const resolvedTitle = title ?? t('domain.filterSheet.title');
  const resolvedTrigger =
    triggerLabel ??
    (activeCount > 0
      ? t('domain.filterSheet.openWithCount', { count: activeCount })
      : t('domain.filterSheet.open'));

  return (
    <div className={className}>
      {showTrigger ? (
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          className="inline-flex min-h-11 items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100 dark:hover:bg-gray-800"
        >
          {resolvedTrigger}
        </button>
      ) : null}

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          role="presentation"
          onClick={closeWithoutApply}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="filter-sheet-title"
            className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-gray-700">
              <h2 id="filter-sheet-title" className="text-sm font-semibold text-gray-950 dark:text-white">
                {resolvedTitle}
              </h2>
              <button
                type="button"
                onClick={closeWithoutApply}
                className="rounded-md px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
              >
                {t('common.close')}
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {fields.map((field) => {
                if (field.type === 'select') {
                  return (
                    <label key={field.id} className="block space-y-1.5 text-sm">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {field.label}
                      </span>
                      <select
                        value={draft[field.id] ?? ''}
                        onChange={(e) => setField(field.id, e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100"
                      >
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                }
                if (field.type === 'search') {
                  return (
                    <label key={field.id} className="block space-y-1.5 text-sm">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {field.label}
                      </span>
                      <input
                        type="search"
                        value={draft[field.id] ?? ''}
                        placeholder={field.placeholder}
                        onChange={(e) => setField(field.id, e.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 dark:border-gray-700 dark:bg-gray-950 dark:text-slate-100"
                      />
                    </label>
                  );
                }
                return (
                  <div key={field.id} className="space-y-1.5">
                    {field.label ? (
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        {field.label}
                      </p>
                    ) : null}
                    {field.render({
                      value: draft[field.id] ?? '',
                      onChange: (value) => setField(field.id, value),
                    })}
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
              {onClearAll ? (
                <button
                  type="button"
                  onClick={() => {
                    onClearAll();
                    onOpenChange(false);
                  }}
                  className="text-sm font-semibold text-slate-600 hover:underline dark:text-slate-300"
                >
                  {t('domain.filterSheet.clearAll')}
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={closeWithoutApply}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-200 dark:hover:bg-gray-800"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onApply(draft);
                    onOpenChange(false);
                  }}
                  className="rounded-xl border border-teal-600 bg-teal-600 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-700 dark:border-teal-500 dark:bg-teal-700 dark:hover:bg-teal-600"
                >
                  {t('domain.filterSheet.apply')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
